import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMarkets } from '../hooks/useMarkets';
import { useCrawl, type CrawlType } from '../hooks/useCrawl';
import {
  useMarketDataByMarket,
  useMarketDataRefresh,
  formatRent,
  formatRentGrowth,
  getRentGrowthClass,
  formatDaysOnMarket,
  formatListings,
} from '../hooks/useMarketData';
import { Loading } from '../components/common';
import { calculateMarketScores, getGrade, getGradeClass } from '../utils/scoring';
import type { Market } from '../types';
import type { MarketDataZip } from '../api/client';
import './MarketsPage.css';

type SortColumn = 'state' | 'count' | 'financial' | 'market' | 'appreciation' | 'landlord' | 'avgScore' | 'name' | 'total';
type ZipSortColumn = 'zip_code' | 'city' | 'median_rent' | 'rent_1br' | 'rent_2br' | 'rent_3br' | 'rent_4br' | 'single_family_rent' | 'rent_growth_pct' | 'days_on_market' | 'total_listings';
type ViewMode = 'states' | 'markets' | 'zips';

interface StateData {
  state: string;
  count: number;
  financial: number;
  market: number;
  appreciation: number;
  landlord: number;
  avgScore: number;
}

interface Filters {
  minYield: number;
  maxPrice: number;
  landlordOnly: boolean;
}

export function MarketsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { markets, loading, error } = useMarkets();
  const { triggerCrawl, loading: crawlLoading } = useCrawl();
  const [crawlingMarket, setCrawlingMarket] = useState<string | null>(null);
  const [crawlMessage, setCrawlMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const stateFromUrl = searchParams.get('state') || '';
  const marketFromUrl = searchParams.get('market') || '';

  const [selectedState, setSelectedState] = useState(stateFromUrl);
  const [selectedMarket, setSelectedMarket] = useState(marketFromUrl);

  // Determine view mode based on selections
  const viewMode: ViewMode = selectedMarket ? 'zips' : selectedState ? 'markets' : 'states';

  const [sortCol, setSortCol] = useState<SortColumn>('avgScore');
  const [sortAsc, setSortAsc] = useState(false);
  const [zipSortCol, setZipSortCol] = useState<ZipSortColumn>('median_rent');
  const [zipSortAsc, setZipSortAsc] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    minYield: 0,
    maxPrice: 9999000,
    landlordOnly: false,
  });

  // Market data for ZIP view
  const { data: marketData, loading: marketDataLoading } = useMarketDataByMarket(
    viewMode === 'zips' ? selectedMarket : null
  );
  const { refresh: refreshMarketData, loading: refreshLoading } = useMarketDataRefresh();

  const handleSort = (col: SortColumn) => {
    if (sortCol === col) {
      setSortAsc(!sortAsc);
    } else {
      setSortCol(col);
      setSortAsc(false);
    }
  };

  const handleZipSort = (col: ZipSortColumn) => {
    if (zipSortCol === col) {
      setZipSortAsc(!zipSortAsc);
    } else {
      setZipSortCol(col);
      setZipSortAsc(false);
    }
  };

  const selectState = (state: string) => {
    setSelectedState(state);
    setSelectedMarket('');
    setSortCol('total');
    setSortAsc(false);
    setSearchParams({ state });
  };

  const selectMarket = (marketName: string) => {
    const slug = marketName.toLowerCase().split(',')[0].replace(/[^a-z]+/g, '_').replace(/^_|_$/g, '');
    setSelectedMarket(slug);
    setSearchParams({ state: selectedState, market: slug });
  };

  const showStates = () => {
    setSelectedState('');
    setSelectedMarket('');
    setSortCol('avgScore');
    setSortAsc(false);
    setSearchParams({});
  };

  const showMarkets = () => {
    setSelectedMarket('');
    setSearchParams({ state: selectedState });
  };

  const goToPropertiesWithZip = (zipCode: string) => {
    navigate(`/properties?market=${selectedMarket}&zip=${zipCode}`);
  };

  const goToMarket = (marketName: string) => {
    const slug = marketName.toLowerCase().split(',')[0].replace(/[^a-z]+/g, '_').replace(/^_|_$/g, '');
    navigate(`/properties?market=${slug}`);
  };

  const handleCrawl = async (marketName: string, crawlType: CrawlType, e: React.MouseEvent) => {
    e.stopPropagation();
    const slug = marketName.toLowerCase().split(',')[0].replace(/[^a-z]+/g, '_').replace(/^_|_$/g, '');
    setCrawlingMarket(slug);
    setCrawlMessage(null);
    try {
      await triggerCrawl(slug, 300000, { crawlType, maxZips: 20 });
      setCrawlMessage({ type: 'success', text: `${crawlType} crawl started for ${marketName}` });
      setTimeout(() => setCrawlMessage(null), 5000);
    } catch (err) {
      setCrawlMessage({ type: 'error', text: `Failed to start crawl: ${err instanceof Error ? err.message : 'Unknown error'}` });
    } finally {
      setCrawlingMarket(null);
    }
  };

  const handleRefreshMarketData = async () => {
    if (!selectedMarket) return;
    setCrawlMessage(null);
    try {
      const result = await refreshMarketData({ market: selectedMarket });
      const zipsRefreshed = result.zipsRefreshed ?? 0;
      const zipsFailed = result.zipsFailed ?? 0;
      setCrawlMessage({
        type: 'success',
        text: `Refreshed ${zipsRefreshed} ZIPs${zipsFailed > 0 ? `, ${zipsFailed} failed` : ''}`,
      });
      setTimeout(() => setCrawlMessage(null), 5000);
    } catch (err) {
      setCrawlMessage({ type: 'error', text: `Failed to refresh: ${err instanceof Error ? err.message : 'Unknown error'}` });
    }
  };

  // Filter markets based on criteria
  const filteredMarkets = useMemo(() => {
    return markets.filter((m) => {
      if ((m.gross_yield_annual || 0) * 100 < filters.minYield) return false;
      if ((m.zhvi_current || 0) > filters.maxPrice) return false;
      if (filters.landlordOnly && (m.landlord_score || 0) < 80) return false;
      return true;
    });
  }, [markets, filters]);

  // Aggregate markets by state
  const stateData = useMemo((): StateData[] => {
    const stateGroups: Record<string, { count: number; totals: { financial: number; market: number; appreciation: number; landlord: number; score: number } }> = {};

    filteredMarkets.forEach((m) => {
      const state = m.state_code || m.state || 'Unknown';
      const scores = calculateMarketScores(m);

      if (!stateGroups[state]) {
        stateGroups[state] = { count: 0, totals: { financial: 0, market: 0, appreciation: 0, landlord: 0, score: 0 } };
      }

      stateGroups[state].count++;
      stateGroups[state].totals.financial += scores.financial;
      stateGroups[state].totals.market += scores.market;
      stateGroups[state].totals.appreciation += scores.appreciation;
      stateGroups[state].totals.landlord += scores.landlord;
      stateGroups[state].totals.score += scores.total;
    });

    return Object.entries(stateGroups)
      .filter(([, data]) => data.count > 0)
      .map(([state, data]) => ({
        state,
        count: data.count,
        financial: Math.round(data.totals.financial / data.count),
        market: Math.round(data.totals.market / data.count),
        appreciation: Math.round(data.totals.appreciation / data.count),
        landlord: Math.round(data.totals.landlord / data.count),
        avgScore: Math.round(data.totals.score / data.count),
      }))
      .sort((a, b) => {
        const aVal = a[sortCol as keyof StateData] || 0;
        const bVal = b[sortCol as keyof StateData] || 0;
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        }
        return sortAsc ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal);
      });
  }, [filteredMarkets, sortCol, sortAsc]);

  // Filter and sort markets for market view
  const stateMarkets = useMemo(() => {
    let filtered = filteredMarkets;
    if (selectedState) {
      filtered = filtered.filter((m) => (m.state_code || m.state) === selectedState);
    }

    return filtered
      .map((m) => ({ ...m, scores: calculateMarketScores(m) }))
      .sort((a, b) => {
        let aVal: number | string;
        let bVal: number | string;

        if (sortCol === 'name') {
          aVal = a.name;
          bVal = b.name;
        } else if (['financial', 'market', 'appreciation', 'landlord', 'total'].includes(sortCol)) {
          aVal = a.scores[sortCol as keyof typeof a.scores];
          bVal = b.scores[sortCol as keyof typeof b.scores];
        } else {
          aVal = (a as Record<string, unknown>)[sortCol] as number || 0;
          bVal = (b as Record<string, unknown>)[sortCol] as number || 0;
        }

        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        }
        return sortAsc ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal);
      });
  }, [filteredMarkets, selectedState, sortCol, sortAsc]);

  // Sort ZIP data
  const sortedZips = useMemo(() => {
    if (!marketData?.zips) return [];
    return [...marketData.zips].sort((a, b) => {
      const aVal = a[zipSortCol] ?? 0;
      const bVal = b[zipSortCol] ?? 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return zipSortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return zipSortAsc ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal);
    });
  }, [marketData?.zips, zipSortCol, zipSortAsc]);

  const getSortClass = (col: SortColumn) => (sortCol === col ? `sorted${sortAsc ? ' asc' : ''}` : '');
  const getZipSortClass = (col: ZipSortColumn) => (zipSortCol === col ? `sorted${zipSortAsc ? ' asc' : ''}` : '');

  if (loading) return <Loading message="Loading markets..." />;
  if (error) return <div className="container error-message">{error}</div>;

  // Get title based on view mode
  const getTitle = () => {
    if (viewMode === 'zips') return `${selectedMarket.replace(/_/g, ' ')} ZIP Codes`;
    if (viewMode === 'markets') return `${selectedState} Markets`;
    return 'Investment Markets by State';
  };

  const getSubtitle = () => {
    if (viewMode === 'zips') return 'RentCast rental market data by ZIP code';
    if (viewMode === 'markets') return 'All markets in state - click to view ZIP details';
    return 'Click a state to see all markets - sorted by investment score';
  };

  return (
    <div className="container">
      <div className="page-header">
        <div>
          <h1>{getTitle()}</h1>
          <p>{getSubtitle()}</p>
        </div>
        {viewMode === 'zips' && (
          <button
            className="btn-refresh"
            onClick={handleRefreshMarketData}
            disabled={refreshLoading}
          >
            {refreshLoading ? 'Refreshing...' : 'Refresh Data'}
          </button>
        )}
      </div>

      {/* Breadcrumb navigation */}
      <div className="breadcrumb">
        <a href="#" onClick={(e) => { e.preventDefault(); showStates(); }}>All States</a>
        {selectedState && (
          <>
            {' > '}
            <a href="#" onClick={(e) => { e.preventDefault(); showMarkets(); }}>{selectedState}</a>
          </>
        )}
        {selectedMarket && (
          <>
            {' > '}
            <span>{selectedMarket.replace(/_/g, ' ')}</span>
          </>
        )}
      </div>

      {crawlMessage && (
        <div className={`crawl-message ${crawlMessage.type}`}>
          {crawlMessage.text}
        </div>
      )}

      {viewMode !== 'zips' && (
        <div className="filter-row">
          <input
            type="number"
            placeholder="Min Yield %"
            style={{ width: 100 }}
            onChange={(e) => setFilters({ ...filters, minYield: parseFloat(e.target.value) || 0 })}
          />
          <input
            type="number"
            placeholder="Max Price ($K)"
            style={{ width: 120 }}
            onChange={(e) => setFilters({ ...filters, maxPrice: (parseFloat(e.target.value) || 9999) * 1000 })}
          />
          <label>
            <input
              type="checkbox"
              checked={filters.landlordOnly}
              onChange={(e) => setFilters({ ...filters, landlordOnly: e.target.checked })}
            />
            Landlord Friendly Only (80+)
          </label>
        </div>
      )}

      {viewMode === 'states' && (
        <StateTable
          states={stateData}
          onSort={handleSort}
          getSortClass={getSortClass}
          onSelectState={selectState}
        />
      )}

      {viewMode === 'markets' && (
        <MarketTable
          markets={stateMarkets}
          onSort={handleSort}
          getSortClass={getSortClass}
          onSelectMarket={selectMarket}
          onGoToProperties={goToMarket}
          onCrawl={handleCrawl}
          crawlingMarket={crawlingMarket}
          crawlLoading={crawlLoading}
        />
      )}

      {viewMode === 'zips' && (
        marketDataLoading ? (
          <Loading message="Loading ZIP data..." />
        ) : marketData?.zips && marketData.zips.length > 0 ? (
          <>
            {/* Market summary */}
            <div className="market-summary">
              <div className="summary-stat">
                <span className="stat-label">ZIPs</span>
                <span className="stat-value">{marketData.zipCount}</span>
              </div>
              <div className="summary-stat">
                <span className="stat-label">Avg Rent</span>
                <span className="stat-value">{formatRent(marketData.avgMedianRent)}</span>
              </div>
              <div className="summary-stat">
                <span className="stat-label">Rent Growth</span>
                <span className={`stat-value ${getRentGrowthClass(marketData.avgRentGrowth)}`}>
                  {formatRentGrowth(marketData.avgRentGrowth)}
                </span>
              </div>
              <div className="summary-stat">
                <span className="stat-label">Avg DOM</span>
                <span className="stat-value">{formatDaysOnMarket(marketData.avgDaysOnMarket)}</span>
              </div>
              <div className="summary-stat">
                <span className="stat-label">Total Listings</span>
                <span className="stat-value">{formatListings(marketData.totalListings)}</span>
              </div>
            </div>

            <ZipTable
              zips={sortedZips}
              onSort={handleZipSort}
              getSortClass={getZipSortClass}
              onSelectZip={goToPropertiesWithZip}
            />
          </>
        ) : (
          <div className="no-data">
            <h3>No Market Data Available</h3>
            <p>Click "Refresh Data" to fetch rental market data for this market.</p>
          </div>
        )
      )}

      <p className="table-footer">
        {viewMode === 'states' && `Showing ${stateData.length} states with ${stateData.reduce((sum, s) => sum + s.count, 0)} total markets.`}
        {viewMode === 'markets' && `Showing ${stateMarkets.length} markets${selectedState ? ` in ${selectedState}` : ''}.`}
        {viewMode === 'zips' && marketData && `Showing ${marketData.zips?.length || 0} ZIP codes in ${selectedMarket.replace(/_/g, ' ')}.`}
      </p>
    </div>
  );
}

// State Table Component
interface StateTableProps {
  states: StateData[];
  onSort: (col: SortColumn) => void;
  getSortClass: (col: SortColumn) => string;
  onSelectState: (state: string) => void;
}

function StateTable({ states, onSort, getSortClass, onSelectState }: StateTableProps) {
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th onClick={() => onSort('state')} className={getSortClass('state')}>State</th>
          <th onClick={() => onSort('count')} className={getSortClass('count')}>#</th>
          <th onClick={() => onSort('financial')} className={getSortClass('financial')}>Financial</th>
          <th onClick={() => onSort('market')} className={getSortClass('market')}>Market</th>
          <th onClick={() => onSort('appreciation')} className={getSortClass('appreciation')}>Apprec.</th>
          <th onClick={() => onSort('landlord')} className={getSortClass('landlord')}>Landlord</th>
          <th onClick={() => onSort('avgScore')} className={getSortClass('avgScore')}>Score</th>
          <th>Grade</th>
        </tr>
      </thead>
      <tbody>
        {states.map((s) => {
          const grade = getGrade(s.avgScore);
          return (
            <tr key={s.state} className="clickable-row" onClick={() => onSelectState(s.state)}>
              <td><span className="state-name">{s.state}</span></td>
              <td>{s.count}</td>
              <td>{s.financial}</td>
              <td>{s.market}</td>
              <td>{s.appreciation}</td>
              <td>{s.landlord}</td>
              <td><span className="investment-score">{s.avgScore}</span></td>
              <td><span className={`grade-badge ${getGradeClass(grade)}`}>{grade}</span></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// Market Table Component
interface MarketTableProps {
  markets: Array<Market & { scores: ReturnType<typeof calculateMarketScores> }>;
  onSort: (col: SortColumn) => void;
  getSortClass: (col: SortColumn) => string;
  onSelectMarket: (name: string) => void;
  onGoToProperties: (name: string) => void;
  onCrawl: (marketName: string, crawlType: CrawlType, e: React.MouseEvent) => void;
  crawlingMarket: string | null;
  crawlLoading: boolean;
}

function MarketTable({ markets, onSort, getSortClass, onSelectMarket, onGoToProperties, onCrawl, crawlingMarket, crawlLoading }: MarketTableProps) {
  const getSlug = (name: string) => name.toLowerCase().split(',')[0].replace(/[^a-z]+/g, '_').replace(/^_|_$/g, '');

  const formatRentValue = (v?: number) => v ? `$${v.toLocaleString()}` : '-';
  const formatYield = (v?: number) => v ? `${(v * 100).toFixed(1)}%` : '-';
  const formatAge = (dateStr?: string) => {
    if (!dateStr) return '-';
    const age = Date.now() - new Date(dateStr).getTime();
    const days = Math.floor(age / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return '1d ago';
    if (days < 7) return `${days}d ago`;
    return `${Math.floor(days / 7)}w ago`;
  };

  return (
    <table className="data-table">
      <thead>
        <tr>
          <th onClick={() => onSort('name')}>Market</th>
          <th onClick={() => onSort('state')} className={getSortClass('state')}>St</th>
          <th onClick={() => onSort('financial')} className={getSortClass('financial')}>Fin</th>
          <th onClick={() => onSort('market')} className={getSortClass('market')}>Mkt</th>
          <th onClick={() => onSort('appreciation')} className={getSortClass('appreciation')}>App</th>
          <th onClick={() => onSort('landlord')} className={getSortClass('landlord')}>LL</th>
          <th onClick={() => onSort('total')} className={getSortClass('total')}>Score</th>
          <th>Grade</th>
          <th className="rental-col" title="Median Rent (live data)">Rent</th>
          <th className="rental-col" title="Gross Yield">Yield</th>
          <th className="rental-col" title="Active Rentals">#</th>
          <th className="rental-col" title="Last Updated">Updated</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {markets.map((m) => {
          const grade = getGrade(m.scores.total);
          const slug = getSlug(m.name);
          const isCrawling = crawlingMarket === slug && crawlLoading;
          const hasRentalData = m.rentals_updated !== undefined;

          return (
            <tr key={m.name} className="clickable-row" onClick={() => onSelectMarket(m.name)}>
              <td><span className="market-name">{m.name}</span></td>
              <td>{m.state_code || m.state}</td>
              <td>{m.scores.financial}</td>
              <td>{m.scores.market}</td>
              <td>{m.scores.appreciation}</td>
              <td>{m.scores.landlord}</td>
              <td><span className="investment-score">{m.scores.total}</span></td>
              <td><span className={`grade-badge ${getGradeClass(grade)}`}>{grade}</span></td>
              <td className={`rental-col ${hasRentalData ? 'live-data' : ''}`}>
                {formatRentValue(m.zori_current)}
              </td>
              <td className={`rental-col ${hasRentalData ? 'live-data' : ''}`}>
                {formatYield(m.gross_yield_annual)}
              </td>
              <td className="rental-col">
                {m.rental_listings || '-'}
              </td>
              <td className="rental-col rental-age">
                {formatAge(m.rentals_updated)}
              </td>
              <td className="actions-cell">
                <div className="crawl-buttons">
                  <button
                    className="btn-crawl btn-crawl-props"
                    onClick={(e) => { e.stopPropagation(); onGoToProperties(m.name); }}
                    title="View properties"
                  >
                    🏠
                  </button>
                  <button
                    className="btn-crawl btn-crawl-data"
                    onClick={(e) => onCrawl(m.name, 'market_data', e)}
                    disabled={isCrawling}
                    title="Collect rental market data (fast)"
                  >
                    {isCrawling ? '...' : '📊'}
                  </button>
                  <button
                    className="btn-crawl btn-crawl-full"
                    onClick={(e) => onCrawl(m.name, 'full', e)}
                    disabled={isCrawling}
                    title="Full crawl (market data + properties)"
                  >
                    {isCrawling ? '...' : '🔄'}
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ZIP Table Component
interface ZipTableProps {
  zips: MarketDataZip[];
  onSort: (col: ZipSortColumn) => void;
  getSortClass: (col: ZipSortColumn) => string;
  onSelectZip: (zip: string) => void;
}

function ZipTable({ zips, onSort, getSortClass, onSelectZip }: ZipTableProps) {
  return (
    <table className="data-table zip-table">
      <thead>
        <tr>
          <th onClick={() => onSort('zip_code')} className={getSortClass('zip_code')}>ZIP</th>
          <th onClick={() => onSort('city')} className={getSortClass('city')}>City</th>
          <th onClick={() => onSort('median_rent')} className={getSortClass('median_rent')}>Median</th>
          <th onClick={() => onSort('rent_1br')} className={getSortClass('rent_1br')}>1BR</th>
          <th onClick={() => onSort('rent_2br')} className={getSortClass('rent_2br')}>2BR</th>
          <th onClick={() => onSort('rent_3br')} className={getSortClass('rent_3br')}>3BR</th>
          <th onClick={() => onSort('rent_4br')} className={getSortClass('rent_4br')}>4BR</th>
          <th onClick={() => onSort('single_family_rent')} className={getSortClass('single_family_rent')}>SF</th>
          <th onClick={() => onSort('rent_growth_pct')} className={getSortClass('rent_growth_pct')}>Growth</th>
          <th onClick={() => onSort('days_on_market')} className={getSortClass('days_on_market')}>DOM</th>
          <th onClick={() => onSort('total_listings')} className={getSortClass('total_listings')}>Listings</th>
        </tr>
      </thead>
      <tbody>
        {zips.map((z) => (
          <tr key={z.zip_code} className="clickable-row" onClick={() => onSelectZip(z.zip_code)}>
            <td><span className="zip-code">{z.zip_code}</span></td>
            <td>{z.city || '-'}</td>
            <td>{formatRent(z.median_rent)}</td>
            <td>{formatRent(z.rent_1br)}</td>
            <td>{formatRent(z.rent_2br)}</td>
            <td>{formatRent(z.rent_3br)}</td>
            <td>{formatRent(z.rent_4br)}</td>
            <td>{formatRent(z.single_family_rent)}</td>
            <td className={getRentGrowthClass(z.rent_growth_pct)}>{formatRentGrowth(z.rent_growth_pct)}</td>
            <td>{formatDaysOnMarket(z.days_on_market)}</td>
            <td>{formatListings(z.total_listings)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
